/**
 * History direction tracker.
 *
 * Determines whether a visit is travelling forward or backward so the router can
 * append the `--reverse` suffix to the transition name, and stamps each history
 * entry with a monotonically increasing `psdIndex` for debugging and for anything
 * that needs to reason about depth.
 *
 * ## Why this does not hand-roll direction from the index
 *
 * It used to. `trackDirection` read `visit.to.state?.psdIndex` and wrote the new
 * index back via `visit.to.state.psdIndex = …`. **Swup v4 never reads or writes
 * `visit.to.state` through to the History API**, so the stamp never reached
 * `window.history.state`: every entry after the first had no `psdIndex`, the
 * `targetIndex !== undefined` guard always failed, and every popstate was
 * therefore classified as *forward*. The consequence shipped silently — the kit's
 * reverse transitions never once played, and `data-transition` never carried the
 * `--reverse` suffix the CSS scopes on.
 *
 * This is the same failure mode as the earlier `visit.dataset` bug in
 * resolveTransition: writing to a property of the visit that Swup does not
 * consult. The lesson both times is to read Swup's own signals rather than
 * invent a parallel channel.
 *
 * So direction now comes from `visit.history` — Swup already tracks it — and the
 * index is written with `history.replaceState` against the entry the browser has
 * actually committed.
 *
 * @see resources/js/router/swup.js
 */

let currentIndex = 0;

/**
 * Stamp the current (initial) history entry with psdIndex: 0.
 */
export function initHistory() {
    const state = window.history.state || {};

    if (state.psdIndex === undefined) {
        window.history.replaceState({ ...state, psdIndex: 0 }, '');
        currentIndex = 0;
    } else {
        currentIndex = state.psdIndex;
    }
}

/**
 * Classify a visit's direction and set `visit.direction`.
 *
 * Runs on `visit:start`, before the swap, so the router can use the result when
 * it resolves the animation name.
 *
 * @param {Object} visit - Swup visit object
 */
export function trackDirection(visit) {
    // Swup's own signal. `history.popstate` is true for back/forward button travel.
    //
    // NOTE THE SPELLING: Swup v4 reports 'backwards' / 'forwards' — PLURAL — even
    // though its own HistoryDirection type reads "backward or forward" in the
    // docblock. Matching the singular silently never fires, which is precisely how
    // reverse transitions stayed dead. Accept both so a future rename cannot
    // reintroduce the bug quietly.
    const direction = visit.history?.direction;
    const backward = Boolean(visit.history?.popstate) && /^backwards?$/.test(direction ?? '');

    visit.direction = backward ? 'backward' : 'forward';

    if (backward) {
        // The browser has already applied the target entry by the time visit:start
        // fires, so its committed state is the authority on depth.
        const targetIndex = window.history.state?.psdIndex;
        currentIndex = typeof targetIndex === 'number' ? targetIndex : Math.max(0, currentIndex - 1);

        if (visit.animation?.name && !visit.animation.name.endsWith('--reverse')) {
            visit.animation.name += '--reverse';
        }

        return;
    }

    if (visit.history?.popstate) {
        // Forward button: take the committed index where present.
        const targetIndex = window.history.state?.psdIndex;
        currentIndex = typeof targetIndex === 'number' ? targetIndex : currentIndex + 1;

        return;
    }

    currentIndex++;
}

/**
 * Write the running index onto the history entry Swup has just pushed.
 *
 * Must run AFTER the push — on `visit:start` the entry does not exist yet, which
 * is precisely why the old `visit.to.state` assignment silently did nothing.
 * A no-op on popstate, where the entry already carries its index.
 *
 * @param {Object} visit - Swup visit object
 */
export function stampHistoryEntry(visit) {
    if (visit.history?.popstate) {
        return;
    }

    const state = window.history.state || {};

    if (state.psdIndex === currentIndex) {
        return;
    }

    window.history.replaceState({ ...state, psdIndex: currentIndex }, '');
}
